import { useEffect, useState } from 'react';
import {
  FlaskConical,
  WandSparkles,
  ChevronLeft,
  ChevronRight,
  Building2,
  Users,
  SlidersHorizontal,
  RotateCcw,
  Database,
  Monitor,
  Smartphone,
  Info,
  Upload,
  Square,
} from 'lucide-react';
import {
  createProgressWebSocketTicket,
  fetchCompanies,
  fetchProducts,
  getProgressWebSocketUrl,
  uploadCampaignReference,
} from '../services/api';
import { useSettings } from '../settings';
import { formatGenerationDurationMs, getGenerationDurationMs } from '../utils/generationTiming';
import PremiumSelect from './PremiumSelect';

const PLATFORM_VIDEO_SPECS = {
  Instagram: { size: '1080x1350', ratio: '4:5', placement: 'mobile feed' },
  'Instagram Story': { size: '1080x1920', ratio: '9:16', placement: 'Stories' },
  Facebook: { size: '1080x1350', ratio: '4:5', placement: 'mobile feed' },
  LinkedIn: { size: '1080x1350', ratio: '4:5', placement: 'mobile feed' },
  X: { size: '1080x1080', ratio: '1:1', placement: 'mobile timeline' },
  TikTok: { size: '1080x1920', ratio: '9:16', placement: 'For You feed' },
  YouTube: { size: '1920x1080', ratio: '16:9', placement: 'in-stream' },
};

const getPlatformVideoSpec = (platform) => PLATFORM_VIDEO_SPECS[platform] || PLATFORM_VIDEO_SPECS.Instagram;

function VideoFormatPreview({ platform, spec }) {
  const isLaptop = spec.ratio === '16:9';
  const formatClass = spec.ratio === '9:16' ? 'is-tall' : spec.ratio === '1:1' ? 'is-square' : 'is-portrait';

  return (
    <div
      className={`video-format-card ${isLaptop ? 'is-laptop' : `is-phone ${formatClass}`}`}
      role="group"
      aria-label={`${platform} video output, ${spec.size} pixels, ${spec.ratio} aspect ratio`}
    >
      <div className="video-device-stage" aria-hidden="true">
        <div className="video-device-shell">
          {!isLaptop && <i className="video-device-speaker" />}
          <div className="video-preview-screen" style={{ aspectRatio: spec.ratio.replace(':', ' / ') }}>
            <strong>{spec.ratio}</strong>
            <small>{isLaptop ? 'Landscape' : spec.ratio === '1:1' ? 'Square' : 'Portrait'}</small>
          </div>
        </div>
        {isLaptop && <div className="video-laptop-base" />}
      </div>
      <div className="video-format-copy">
        <div className="video-format-heading">
          <span className="video-format-icon">{isLaptop ? <Monitor size={17} /> : <Smartphone size={17} />}</span>
          <div>
            <small>{isLaptop ? 'Landscape display' : 'Mobile-first display'}</small>
            <strong>{platform} {spec.placement}</strong>
          </div>
        </div>
        <div className="video-format-specs">
          <span><small>Canvas</small><strong>{spec.size} px</strong></span>
          <span><small>Ratio</small><strong>{spec.ratio}</strong></span>
        </div>
        <p>The preview scales to the shape viewers will see when this campaign is published.</p>
      </div>
    </div>
  );
}

const initialForm = {
  brand_name: '',
  product_name: '',
  product_description: '',
  target_audience: '',
  campaign_goal: 'Increase product awareness and online sales',
  tone: 'Modern, friendly and motivating',
  platform: 'Instagram',
  language: 'English',
  special_offer: '',
  key_benefits: '',
  desired_visual_style: 'Premium modern social ad with gradients and clean typography',
  competitor_or_reference: '',
  generation_strategy: 'Balanced',
  video_duration: 8,
  video_fps: 24,
  video_size: '1080x1350',
  video_text_mode: 'overlay',
  generate_video: false,
  product_keywords: '',
  product_features: '',
  visual_instructions: '',
  negative_instructions: '',
  product_image_urls: '',
  product_image_mode: 'reference',
  brand_logo_url: '',
  product_category: '',
  company_id: '',
  product_id: '',
  render_style: 'auto',
  poster_style: 'auto',
  use_brand_assets: true,
};

const RENDER_STYLES = [
  ['auto', 'Auto (smart)'],
  ['realistic', 'Realistic'],
  ['animated', 'Animated'],
];

const VISUAL_STYLE_OPTIONS = [
  'Premium modern social ad with gradients and clean typography',
  'Minimal luxury poster with elegant negative space',
  'Bold high-contrast product hero ad',
  'Tech editorial dashboard-style creative',
  'Cinematic launch ad with dramatic product spotlight',
  'Emotional lifestyle storytelling scene',
  'Playful colorful social media creative',
  'Professional B2B trust-building visual',
];

const POSTER_STYLES = [
  ['auto', 'Auto art direction'],
  ['minimal_luxury', 'Minimal luxury'],
  ['bold_fmcg', 'Bold FMCG / product hero'],
  ['editorial_heritage', 'Editorial heritage'],
  ['object_metaphor', 'Object metaphor'],
  ['sport_energy', 'Sport energy'],
  ['food_offer', 'Food offer'],
  ['tech_editorial', 'Tech editorial'],
  ['cinematic_launch', 'Cinematic launch'],
  ['classic_longcopy', 'Classic long-copy'],
];

const STEPS = [
  { id: 0, labelKey: 'studio.brandProduct', icon: Building2 },
  { id: 1, labelKey: 'studio.audienceValue', icon: Users },
  { id: 2, labelKey: 'studio.strategyOutput', icon: SlidersHorizontal },
];

function GeneratorForm({
  form,
  setForm,
  loading,
  loadingMode,
  generationJob,
  generationJobId,
  campaign,
  generationStartedAtMs,
  onGenerate,
  onExperiment,
  onStopGeneration,
  stoppingGeneration = false,
}) {
  const [progressMsg, setProgressMsg] = useState('');
  const [step, setStep] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [referenceUploadStatus, setReferenceUploadStatus] = useState('');
  const [referenceUploads, setReferenceUploads] = useState(0);
  const [timingClock, setTimingClock] = useState(Date.now());
  const { t } = useSettings();

  useEffect(() => {
    if (!loading) return undefined;
    setTimingClock(Date.now());
    const timer = window.setInterval(() => setTimingClock(Date.now()), 250);
    return () => window.clearInterval(timer);
  }, [loading, generationStartedAtMs]);

  useEffect(() => {
    if (!loading || !generationJobId) {
      setProgressMsg('');
      return undefined;
    }

    let socket;
    let retryTimer;
    let cancelled = false;

    const connect = async () => {
      try {
        const { ticket } = await createProgressWebSocketTicket(generationJobId);
        if (cancelled) return;
        socket = new WebSocket(getProgressWebSocketUrl(generationJobId, ticket));
        socket.onmessage = (event) => {
          try {
            const update = JSON.parse(event.data);
            setProgressMsg(update.message || 'Generating locally...');
          } catch (e) {
            setProgressMsg(event.data);
          }
        };
        socket.onerror = () => {
          setProgressMsg((current) => current || 'Generating locally...');
        };
        socket.onclose = () => {
          if (!cancelled && loading) {
            retryTimer = window.setTimeout(() => { connect(); }, 1500);
          }
        };
      } catch (e) {
        setProgressMsg('Generating locally...');
      }
    };

    connect();
    return () => {
      cancelled = true;
      window.clearTimeout(retryTimer);
      if (socket && socket.readyState <= 1) socket.close();
    };
  }, [loading, generationJobId]);

  useEffect(() => {
    fetchCompanies()
      .then((data) => setCompanies(data.companies || []))
      .catch(() => setCompanies([]));
  }, []);

  useEffect(() => {
    if (form.company_id) {
      fetchProducts(form.company_id)
        .then((data) => setProducts(data.products || []))
        .catch(() => setProducts([]));
    } else {
      setProducts([]);
    }
  }, [form.company_id]);

  const update = (event) => {
    const { name, value } = event.target;
    if (name === 'platform') {
      setForm((current) => ({
        ...current,
        platform: value,
        video_size: getPlatformVideoSpec(value).size,
      }));
      return;
    }
    const parsedValue = ['video_duration', 'video_fps'].includes(name) ? Number(value) : value;
    setForm((current) => ({
      ...current,
      [name]: name === 'video_duration' ? Math.max(2, Math.min(parsedValue, 10)) : parsedValue,
    }));
  };

  // Selecting a saved company switches to Saved Company Mode. We intentionally
  // do NOT copy the company/product details into the manual text fields: in
  // saved mode the backend uses the stored profile directly, and keeping the
  // manual fields untouched means switching back to Manual Input Mode never
  // reuses the previously selected company's data.
  const handleCompanyGround = (e) => {
    const companyId = e.target.value;
    setForm((current) => ({
      ...current,
      company_id: companyId,
      product_id: '',
    }));
  };

  const handleProductGround = (e) => {
    setForm((current) => ({
      ...current,
      product_id: e.target.value,
    }));
  };

  const handleProductReferenceUpload = async (event) => {
    const exact = form.product_image_mode === 'exact_artwork';
    const files = Array.from(event.target.files || []).slice(0, exact ? 1 : 4);
    if (!files.length) return;
    setReferenceUploads((count) => count + 1);
    setReferenceUploadStatus(`Uploading ${files.length} product reference${files.length === 1 ? '' : 's'}...`);
    try {
      const uploaded = [];
      for (const file of files) {
        const result = await uploadCampaignReference(file, exact ? 'exact_artwork' : 'product');
        const reference = result.url_relative || result.url;
        if (reference) uploaded.push(reference);
      }
      setForm((current) => {
        const existing = String(current.product_image_urls || '')
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);
        return {
          ...current,
          product_image_urls: (exact ? [...uploaded, ...existing.filter((value) => !uploaded.includes(value))]
            : [...existing, ...uploaded]).slice(0, 4).join(', '),
        };
      });
      setReferenceUploadStatus(
        `${uploaded.length} product view${uploaded.length === 1 ? '' : 's'} ready for identity grounding.`
      );
    } catch (error) {
      setReferenceUploadStatus(error.message || 'Product reference upload failed.');
    } finally {
      setReferenceUploads((count) => Math.max(0, count - 1));
      event.target.value = '';
    }
  };

  const handleLogoReferenceUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setReferenceUploads((count) => count + 1);
    setReferenceUploadStatus('Uploading and normalizing the exact logo...');
    try {
      const result = await uploadCampaignReference(file, 'logo');
      const reference = result.url_relative || result.url;
      setForm((current) => ({ ...current, brand_logo_url: reference || '' }));
      setReferenceUploadStatus('Exact logo ready for verified product-surface integration.');
    } catch (error) {
      setReferenceUploadStatus(error.message || 'Logo upload failed.');
    } finally {
      setReferenceUploads((count) => Math.max(0, count - 1));
      event.target.value = '';
    }
  };

  const resetDemo = () => {
    setForm(initialForm);
    setStep(0);
  };

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--mouse-x', `${e.clientX - rect.left}px`);
    e.currentTarget.style.setProperty('--mouse-y', `${e.clientY - rect.top}px`);
  };

  const groundedCompany = companies.find((c) => c.id === form.company_id);
  const groundedProduct = products.find((p) => p.id === form.product_id);
  const platformVideoSpec = getPlatformVideoSpec(form.platform);
  // Saved Company Mode when a real saved company is selected; otherwise Manual
  // Input Mode. This single flag drives which fields are shown and validated.
  const savedMode = !!groundedCompany;
  const missingArtwork = form.product_image_mode === 'exact_artwork'
    && !String(form.product_image_urls || '').split(',').some((value) => value.trim())
    && !(savedMode && form.use_brand_assets !== false);
  const artworkMessage = 'Exact artwork needs a transparent product PNG. Upload one, or choose generation from the product description.';
  const inputsBlocked = missingArtwork || referenceUploads > 0;
  const submitGeneration = (event, handler) => {
    if (inputsBlocked) {
      event.preventDefault();
      setReferenceUploadStatus(missingArtwork ? artworkMessage : 'Wait for the reference upload to finish.');
      return;
    }
    return handler(event);
  };
  const generationStages = ['Brief', 'Brand context', 'Campaign copy', 'Creative render', 'Quality review'];
  const progressSignal = `${generationJob?.status || ''} ${progressMsg || ''}`.toLowerCase();
  let generationStageIndex = 0;
  if (/ground|brand|context|research|analy/.test(progressSignal)) generationStageIndex = 1;
  if (/copy|strategy|prompt|content|writing/.test(progressSignal)) generationStageIndex = 2;
  if (/image|video|render|creative|media|asset/.test(progressSignal)) generationStageIndex = 3;
  if (/quality|evaluat|final|complete|finish|save/.test(progressSignal)) generationStageIndex = 4;
  const completedDurationMs = getGenerationDurationMs(campaign);
  const liveDurationMs = loading && Number.isFinite(Number(generationStartedAtMs))
    ? Math.max(0, timingClock - Number(generationStartedAtMs))
    : null;
  const totalDurationMs = liveDurationMs ?? completedDurationMs;
  const generationComplete = !loading && completedDurationMs != null;
  if (generationComplete) generationStageIndex = generationStages.length;

  const canProceed = () => {
    if (step === 0) {
      if (savedMode) return true;
      return form.brand_name.trim() && form.product_name.trim() && form.product_description.trim();
    }
    if (step === 1) {
      if (inputsBlocked) return false;
      if (savedMode) return true;
      return form.target_audience.trim();
    }
    return true;
  };

  const goNext = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const goBack = () => setStep((s) => Math.max(0, s - 1));

  const isLast = step === STEPS.length - 1;

  // Prevent Enter inside single-line inputs from submitting the form mid-wizard.
  const handleFormKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.tagName === 'INPUT') {
      e.preventDefault();
      if (!isLast && canProceed()) goNext();
    }
  };

  return (
    <section className="panel brief-studio glow-card" onMouseMove={handleMouseMove}>
      <div className="brief-head">
        <div>
          <p className="eyebrow">{t('studio.brief')}</p>
          <h2 className="brief-title">{t('studio.compose')}</h2>
        </div>
        <button type="button" className="brief-reset" onClick={resetDemo} disabled={loading} title="Reset to demo data">
          <RotateCcw size={15} /> {t('studio.reset')}
        </button>
      </div>

      {/* Stepper */}
      <div className="brief-progress">
        {STEPS.map((s, index) => {
          const Icon = s.icon;
          const state = step === index ? 'active' : index < step ? 'complete' : '';
          return (
            <button
              key={s.id}
              type="button"
              data-tour={['studio-step-brand', 'studio-step-audience', 'studio-step-output'][index]}
              className={`brief-step ${state}`}
              onClick={() => setStep(index)}
              disabled={loading}
            >
              <span className="brief-step-badge"><Icon size={16} /></span>
              <span className="brief-step-text">
                <small>{t('studio.step')} {index + 1}</small>
                {t(s.labelKey)}
              </span>
            </button>
          );
        })}
        <div className="brief-progress-bar">
          <div className="brief-progress-fill" style={{ width: `${(step / (STEPS.length - 1)) * 100}%` }} />
        </div>
      </div>

      <form className="brief-form" onSubmit={(event) => submitGeneration(event, onGenerate)} onKeyDown={handleFormKeyDown}>
        {missingArtwork && <p role="alert" className="note-box">{artworkMessage}</p>}
        {/* STEP 1 - Brand & Product */}
        {step === 0 && (
          <div className="brief-section reveal-up">
            <div className="brief-ground">
              <div className="brief-ground-head">
                <Database size={16} />
                <div>
                  <strong>{t('studio.choose')}</strong>
                  <span>{t('studio.chooseHelp')}</span>
                </div>
              </div>
              <div className="form-grid two-columns">
                <label>
                  {t('studio.company')}
                  <PremiumSelect
                    name="company_id"
                    value={form.company_id || ''}
                    onChange={handleCompanyGround}
                    ariaLabel={t('studio.company')}
                    options={[
                      { value: '', label: t('studio.manualInput') },
                      ...companies.map((company) => ({ value: company.id, label: company.company_name })),
                    ]}
                  />
                </label>
                <label>
                  {t('studio.product')}
                  <PremiumSelect
                    name="product_id"
                    value={form.product_id || ''}
                    onChange={handleProductGround}
                    disabled={!form.company_id}
                    ariaLabel={t('studio.product')}
                    options={[
                      { value: '', label: form.company_id ? t('studio.allProducts') : '—' },
                      ...products.map((product) => ({ value: product.id, label: product.product_name })),
                    ]}
                  />
                </label>
              </div>
              {savedMode ? (
                <>
                  <label className="brief-check">
                    <input
                      type="checkbox"
                      checked={!!form.use_brand_assets}
                      onChange={(e) => setForm((c) => ({ ...c, use_brand_assets: e.target.checked }))}
                    />
                    Use my logo, product images and uploaded brand files inside the generated creatives
                  </label>
                  <p className="brief-ground-active">
                    ✓ <strong>{t('studio.savedMode')}</strong> - every image and video uses <strong>{groundedCompany.company_name}</strong>'s saved profile{groundedProduct ? <> and the product <strong>{groundedProduct.product_name}</strong></> : ''}. Manual brand fields are ignored in this mode.
                  </p>
                </>
              ) : (
                <p className="brief-ground-active manual">
                  ✎ <strong>{t('studio.manualMode')}</strong> - {t('studio.manualModeHelp')}
                </p>
              )}
            </div>

            {savedMode ? (
              <div className="brief-saved-summary">
                <div className="brief-subhead">Saved company profile in use</div>
                <ul className="brief-summary-list">
                  <li><span>Brand</span><strong>{groundedCompany.company_name}</strong></li>
                  {groundedProduct && <li><span>Product</span><strong>{groundedProduct.product_name}</strong></li>}
                  {(groundedProduct?.target_market || groundedCompany.target_audience) && (
                    <li><span>Audience</span><strong>{groundedProduct?.target_market || groundedCompany.target_audience}</strong></li>
                  )}
                  {Array.isArray(groundedCompany.colors) && groundedCompany.colors.length > 0 && (
                    <li>
                      <span>Colors</span>
                      <span className="brief-color-dots">
                        {groundedCompany.colors.slice(0, 6).map((col, i) => (
                          <i key={`${col}-${i}`} style={{ background: col }} title={col} />
                        ))}
                      </span>
                    </li>
                  )}
                </ul>
                <p className="brief-hint">Edit any of these in the Brand Hub. The creative settings in the next steps still apply.</p>
              </div>
            ) : (
              <>
                <div className="form-grid two-columns">
                  <label>{t('studio.brandName')}<input name="brand_name" value={form.brand_name} onChange={update} required /></label>
                  <label>{t('studio.productName')}<input name="product_name" value={form.product_name} onChange={update} required /></label>
                </div>
                <label className="brief-block">
                  {t('studio.productDescription')}
                  <textarea name="product_description" value={form.product_description} onChange={update} rows="3" required placeholder={t('studio.productDescriptionPlaceholder')} />
                </label>
                <div className="form-grid two-columns">
                  <label className="brief-block">
                    Product reference views
                    <span className="brief-upload-control">
                      <Upload size={14} />
                      Upload up to 4 product angles
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        multiple
                        aria-label="Upload product reference views"
                        onChange={handleProductReferenceUpload}
                        disabled={loading}
                      />
                    </span>
                    <span className="brief-hint">
                      Front, side, packaging, and detail views improve identity preservation.
                    </span>
                  </label>
                  <label className="brief-block">
                    Exact brand logo
                    <span className="brief-upload-control">
                      <Upload size={14} />
                      Upload logo
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/svg+xml"
                        aria-label="Upload exact brand logo"
                        onChange={handleLogoReferenceUpload}
                        disabled={loading}
                      />
                    </span>
                    <span className="brief-hint">
                      The logo is normalized, physically composited, and visibility-verified after generation.
                    </span>
                  </label>
                </div>
                {referenceUploadStatus && (
                  <p className="brief-ground-active manual" role="status">{referenceUploadStatus}</p>
                )}
              </>
            )}
          </div>
        )}

        {/* STEP 2 - Audience & Value */}
        {step === 1 && (
          <div className="brief-section reveal-up">
            {savedMode ? (
              <p className="brief-hint brief-saved-note">
                Target audience and key benefits come from <strong>{groundedCompany.company_name}</strong>'s saved profile. Use the fields below to shape this specific campaign.
              </p>
            ) : (
              <div className="form-grid two-columns">
                <label>{t('studio.targetAudience')}<textarea name="target_audience" value={form.target_audience} onChange={update} rows="3" required placeholder={t('studio.targetAudiencePlaceholder')} /></label>
                <label>{t('studio.keyBenefits')}<textarea name="key_benefits" value={form.key_benefits} onChange={update} rows="3" placeholder={t('studio.keyBenefitsPlaceholder')} /></label>
              </div>
            )}
            <div className="form-grid two-columns">
              <label>{t('studio.specialOffer')}<input name="special_offer" value={form.special_offer} onChange={update} placeholder={t('studio.specialOfferPlaceholder')} /></label>
              <label>{t('studio.reference')}<input name="competitor_or_reference" value={form.competitor_or_reference} onChange={update} placeholder={t('studio.referencePlaceholder')} /></label>
            </div>
            <label className="brief-block">
              {t('studio.visualStyle')}
              <PremiumSelect name="desired_visual_style" value={form.desired_visual_style || VISUAL_STYLE_OPTIONS[0]} onChange={update} options={VISUAL_STYLE_OPTIONS} ariaLabel={t('studio.visualStyle')} />
            </label>
            <label className="brief-block">
              {t('studio.visualInstructions')}
              <textarea
                name="visual_instructions"
                aria-label={t('studio.visualInstructions')}
                value={form.visual_instructions || ''}
                onChange={update}
                rows="3"
                placeholder={t('studio.visualInstructionsPlaceholder')}
              />
            </label>
            <label className="brief-block">
              Product image treatment
              <select name="product_image_mode" value={form.product_image_mode || 'reference'} onChange={update}>
                <option value="reference">Generate using product references</option>
                <option value="exact_artwork">Preserve exact transparent product artwork</option>
              </select>
              <span className="brief-hint">Exact artwork uses your first uploaded transparent PNG without redrawing its product or label.</span>
              {form.product_image_mode === 'exact_artwork' && (
                <input type="file" accept="image/png" aria-label="Upload exact product artwork"
                  onChange={handleProductReferenceUpload} disabled={loading || referenceUploads > 0} />
              )}
            </label>
            <label className="brief-block">
              {t('studio.negativeInstructions')}
              <textarea
                name="negative_instructions"
                aria-label={t('studio.negativeInstructions')}
                value={form.negative_instructions || ''}
                onChange={update}
                rows="3"
                placeholder={t('studio.negativeInstructionsPlaceholder')}
              />
              <span className="brief-hint">{t('studio.negativeInstructionsHelp')}</span>
            </label>
          </div>
        )}

        {/* STEP 3 - Strategy & Output */}
        {step === 2 && (
          <div className="brief-section reveal-up">
            <div className="form-grid three-columns">
              <label>{t('studio.goal')}
                <PremiumSelect name="campaign_goal" value={form.campaign_goal} onChange={update} ariaLabel={t('studio.goal')}
                  options={['Increase product awareness and online sales', 'Generate leads', 'Launch a new product', 'Improve brand trust', 'Promote a discount campaign']} />
              </label>
              <label>{t('studio.tone')}
                <PremiumSelect name="tone" value={form.tone} onChange={update} ariaLabel={t('studio.tone')}
                  options={['Modern, friendly and motivating', 'Professional and trustworthy', 'Luxury and elegant', 'Funny and casual', 'Emotional and storytelling']} />
              </label>
              <label>{t('studio.strategy')}
                <PremiumSelect name="generation_strategy" value={form.generation_strategy} onChange={update} ariaLabel={t('studio.strategy')}
                  options={['Balanced', 'AIDA', 'PAS', 'Benefit-Focused', 'Trust-Focused']} />
              </label>
            </div>
            <div className="form-grid two-columns">
              <label>{t('studio.platform')}
                <PremiumSelect name="platform" value={form.platform} onChange={update} ariaLabel={t('studio.platform')}
                  options={['Instagram', 'Instagram Story', 'Facebook', 'LinkedIn', 'X', 'TikTok', 'YouTube']} />
              </label>
              <label>{t('studio.language')}
                <PremiumSelect name="language" value={form.language} onChange={update} ariaLabel={t('studio.language')}
                  options={['English', 'German', 'Simple English']} />
              </label>
            </div>
            <label className="brief-block">
              {t('studio.renderStyle')}
              <div className="brief-seg">
                {RENDER_STYLES.map(([val, label]) => (
                  <button
                    type="button"
                    key={val}
                    className={`brief-seg-btn ${form.render_style === val ? 'active' : ''}`}
                    onClick={() => setForm((c) => ({ ...c, render_style: val }))}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <span className="brief-hint">
                Auto uses realistic commercial photography. Choose Animated only when you explicitly want an illustrated/CGI image{form.generate_video ? ' and video' : ''}.
              </span>
            </label>

            <label className="brief-block">
              {t('studio.artDirection')}
              <PremiumSelect
                name="poster_style"
                value={form.poster_style || 'auto'}
                onChange={update}
                options={POSTER_STYLES}
                ariaLabel={t('studio.artDirection')}
              />
              <span className="brief-hint">This controls the poster/layout style used by the prompt and renderer.</span>
            </label>

            <div className="brief-block video-generation-control">
              <label className="settings-toggle-row video-generation-toggle">
                <span>
                  <strong>Video generation</strong>
                  <small>Include a generated video in this campaign.</small>
                </span>
                <input
                  type="checkbox"
                  checked={!!form.generate_video}
                  onChange={(event) => setForm((current) => ({ ...current, generate_video: event.target.checked }))}
                  aria-label="Video generation"
                  disabled={loading}
                />
                <i aria-hidden="true" />
              </label>
              {form.generate_video && (
                <div className="video-generation-notice" role="note">
                  <Info size={16} aria-hidden="true" />
                  <span>Video generation may take up to one hour.</span>
                </div>
              )}
            </div>

            {form.generate_video && (
              <>
                <label className="brief-block">
                  <span className="brief-slider-label">{t('studio.videoDuration')} <strong>{Math.min(form.video_duration, 10)}s</strong></span>
                  <div className="brief-slider">
                    <input type="range" name="video_duration" min="2" max="10" value={Math.min(form.video_duration, 10)} onChange={update} />
                  </div>
                  <span className="brief-hint">Choose 2-10 seconds. The generated and rendered video follows this exact duration.</span>
                </label>
                <div className="brief-block video-format-field">
                  <span className="video-format-label">{t('studio.videoSize')}</span>
                  <VideoFormatPreview platform={form.platform} spec={platformVideoSpec} />
                  <span className="brief-hint">Final MP4: {platformVideoSpec.size.replace('x', ' × ')} pixels at 24 FPS. Locked to the correct {form.platform} {platformVideoSpec.placement} format.</span>
                </div>
              </>
            )}

          </div>
        )}

        {(loading || generationComplete) && (
          <div className="generation-storyboard" role="status" aria-live="polite">
            <div className="generation-storyboard-head">
              <span className={`brief-pulse ${generationComplete ? 'complete' : ''}`} />
              <div className="generation-storyboard-copy">
                <strong>{generationComplete ? 'Campaign generated' : 'Building your campaign system'}</strong>
                <small>{generationComplete
                  ? `Completed in ${formatGenerationDurationMs(completedDurationMs)}. This duration is saved with the campaign.`
                  : stoppingGeneration
                    ? 'Stopping generation and releasing the GPU…'
                    : (generationJob?.status
                      ? `Job ${generationJob.status}: ${progressMsg || 'Preparing generation…'}`
                      : (progressMsg || 'Connecting to generation engine…'))}</small>
              </div>
              <div className="generation-timing" aria-label="Generation timing">
                <span className={generationComplete ? 'complete' : ''}>
                  <small>Total</small>
                  <strong>{totalDurationMs == null ? 'Starting…' : formatGenerationDurationMs(totalDurationMs)}</strong>
                </span>
              </div>
              {loading && onStopGeneration && (
                <button
                  type="button"
                  className="generation-stop-button"
                  onClick={onStopGeneration}
                  disabled={stoppingGeneration || !generationJobId}
                  title={generationJobId
                    ? 'Stop this generation and release the GPU'
                    : 'Waiting for the generation job to start…'}
                >
                  <Square size={14} aria-hidden="true" />
                  {stoppingGeneration ? 'Stopping…' : 'Stop generation'}
                </button>
              )}
            </div>
            <div className="generation-stage-track">
              {generationStages.map((stageName, index) => (
                <div key={stageName} className={`generation-stage ${index < generationStageIndex ? 'complete' : ''} ${index === generationStageIndex ? 'active' : ''}`}>
                  <i>{index < generationStageIndex ? '✓' : index + 1}</i>
                  <span>{stageName}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer navigation / actions */}
        <div className="brief-footer">
          <button type="button" className="secondary-button" onClick={goBack} disabled={step === 0 || loading} style={{ visibility: step === 0 ? 'hidden' : 'visible' }}>
            <ChevronLeft size={16} /> {t('studio.back')}
          </button>

          <div className="brief-footer-right">
            {!isLast ? (
              <button type="button" className="primary-button" onClick={goNext} disabled={!canProceed() || loading}>
                {t('studio.continue')} <ChevronRight size={16} />
              </button>
            ) : (
              <>
                <button type="button" className="secondary-button experiment-button" onClick={(event) => submitGeneration(event, onExperiment)} disabled={loading || inputsBlocked}>
                  <FlaskConical size={16} />
                  {loading && loadingMode === 'experiment' ? t('studio.running') : t('studio.experiment')}
                </button>
                <button type="submit" className="primary-button" disabled={loading || inputsBlocked}>
                  <WandSparkles size={16} />
                  {loading && loadingMode === 'single' ? t('studio.generating') : t('studio.generate')}
                </button>
              </>
            )}
          </div>
        </div>
      </form>
    </section>
  );
}

export { initialForm };
export default GeneratorForm;
